import React from 'react';
import { LayoutEvent } from './rn-utils.types';
import useElementLayout from './useElementLayout';

type ExtendedProps<P> = P & { onLayout?: (e: LayoutEvent) => void };

type ExtendedComponentType<P extends object, H extends any> = React.ForwardRefExoticComponent<
  ExtendedProps<P> & React.RefAttributes<H>
>;

/**
 * HOC to add layout effect to component
 * @param WrappedComponent Component to add onLayout functionality
 * @returns Wrapped object
 */
const withLayout = <P extends object, H extends any>(
  WrappedComponent: React.ComponentType<P>,
): ExtendedComponentType<P, H> => {
  const Instance = React.forwardRef(
    (props: ExtendedProps<P>, forwardedRef: React.Ref<H | null>) => {
      const { onLayout, ...rest } = props;
      const hostRef = React.useRef<H>(null);
      React.useImperativeHandle(forwardedRef, () => hostRef.current);
      useElementLayout(hostRef, onLayout);

      // @ts-expect-error weird onLayout omit error
      return <WrappedComponent {...rest} ref={hostRef} />;
    },
  );

  Instance.displayName = `WithLayout-${WrappedComponent.displayName}`;

  return Instance as ExtendedComponentType<P, H>;
};

export default withLayout;
